import type { TutorialViewModel } from '@trinnvis/dabih-api-client';
import type { TutorialRegistration } from '@trinnvis/dabih-api-client/src/models/TutorialRegistration';
import { numberWord } from 'src/utilities/text';
import type { TutorialRegistrationParticipant } from '@trinnvis/dabih-api-client/src/models/TutorialRegistrationParticipant';
import { sortBy } from 'lodash';
import { LocalDate } from 'src/utilities/local-date';
import { t } from 'src/i18n';

/**
 * Calculate secondary label for tutorial showing completion statistics.
 * Counts unique employees (by employeeUuid) and how many have completed at least once.
 *
 * Handles cases where:
 * - Same employee has multiple registrations (e.g., completed once, in-progress once)
 * - completed field can be null, empty string, or ISO date string
 * - Guides have null employeeUuid and should not be counted
 */

function courseVersiontext(registrations: TutorialRegistration[]): string {
  // Get all participants from all registrations
  const allParticipants = registrations?.flatMap((r) => r.participants) || [];
  // Group by employeeUuid to deduplicate same person across multiple registrations
  const employeeMap = new Map<string, boolean>();
  allParticipants.forEach((p) => {
    // Skip if no employeeUuid (shouldn't happen for courses, but be safe)
    if (!p || !p.employeeUuid) return;
    const employeeId = p.employeeUuid;
    const hasCompleted = !!(p.completed && p.completed !== '');
    // If employee already in map, update completion status (true if any registration is completed)
    if (employeeMap.has(employeeId)) {
      const currentStatus = employeeMap.get(employeeId)!;
      employeeMap.set(employeeId, currentStatus || hasCompleted);
    } else {
      employeeMap.set(employeeId, hasCompleted);
    }
  });
  const totalCount = employeeMap.size;
  const completedCount = Array.from(employeeMap.values()).filter((completed) => completed).length;
  return totalCount === 0
    ? t('tutorials.noneRegistered')
    : t('tutorials.completedCount', { count: numberWord(completedCount) });
}

function guideVersionText(registrations: TutorialRegistration[]): string {
  const implementations: TutorialRegistrationParticipant[] = [];
  registrations.forEach((r) => {
    if (r.participants) {
      implementations.push(r.participants[0]);
    }
  });
  if (implementations.length > 0) {
    const completed = implementations.filter((i) => {
      return i.status === 'COMPLETED';
    });
    if (completed.length > 0) {
      const lastCompleted = sortBy(completed, [(i) => i.completed]).reverse()[0].completed;
      return t('tutorials.lastCompletedOn', { date: LocalDate.fromString(lastCompleted).toStringForDisplay() });
    }
  }
  return t('tutorials.notCompleted');
}

export function secondaryLabelForTutorial(tutorial: TutorialViewModel): string {
  if (tutorial.guide) {
    return guideVersionText(tutorial.registrations);
  }
  const registrationsOnNewVersion = tutorial.registrations.filter(
    (r) => r.contentVersionId === tutorial.contentVersionId,
  );
  const registrationsOnOldVersions = tutorial.registrations.filter(
    (r) => r.contentVersionId !== tutorial.contentVersionId,
  );
  if (registrationsOnOldVersions.length > 0) {
    return t('tutorials.completionNewAndOldVersions', {
      newest: courseVersiontext(registrationsOnNewVersion),
      previous: courseVersiontext(registrationsOnOldVersions),
    });
  }
  return courseVersiontext(registrationsOnNewVersion);
}
